import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group';
import type React from 'react';

export type SegmentedToggleOption<TValue extends string> = {
  value: TValue;
  label: React.ReactNode;
  disabled?: boolean;
  badge?: React.ReactNode;
};

type SegmentedToggleProperties<TValue extends string> = {
  value: TValue;
  options: readonly SegmentedToggleOption<TValue>[];
  onChange: (value: TValue) => void;
  className?: string;
};

export function SegmentedToggle<TValue extends string>({
  value,
  options,
  onChange,
  className,
}: SegmentedToggleProperties<TValue>) {
  return (
    <ToggleGroupPrimitive.Root
      type="single"
      value={value}
      onValueChange={(next) => {
        // Radix emits "" on deselect attempts; this is a radio pattern, swallow it.
        if (next === '') {
          return;
        }

        onChange(next as TValue);
      }}
      // Frame lives on a ::after overlay so it paints above the segment
      // backgrounds (last in render order). On focus, the toolbar-edge
      // frame swaps to toolbar-edge-focus in place — same thickness, just
      // a shade flip, matching the inputClass focus pattern.
      className={`relative inline-flex rounded-sm shadow-toolbar shrink-0 after:pointer-events-none after:absolute after:inset-0 after:rounded-sm after:ring-1 after:ring-inset after:ring-toolbar-edge after:content-[''] has-[:focus-visible]:after:ring-toolbar-edge-focus ${className ?? ''}`}
    >
      {options.map((option) => (
        <ToggleGroupPrimitive.Item
          key={option.value}
          value={option.value}
          disabled={option.disabled === true}
          className="cursor-pointer bg-white px-2 py-2 text-sm leading-6 text-gray-600 first:rounded-l-sm last:rounded-r-sm hover:bg-gray-50 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-white data-[state=on]:bg-primary-500 data-[state=on]:text-white data-[state=on]:hover:bg-primary-500"
        >
          <span className="inline-flex items-center">
            {option.label}
            {option.badge}
          </span>
        </ToggleGroupPrimitive.Item>
      ))}
    </ToggleGroupPrimitive.Root>
  );
}
